import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { registerSmallTierLoad, waitForLargeTierGate } from '@/utils/progressiveTierGate';
import { planImageUpgrade, planVideoUpgrade, recordResourceThroughput, recordTransferThroughput } from '@/utils/abrQuality';

export interface ProgressiveTierSources {
  /** Tiny, near-instant asset — typically the classic ~96px Library thumb
   *  (`thumbUrlFromMediaSrc`), which already exists for virtually every
   *  published item. Shown immediately as the initial `src`, before any of
   *  the real tiers below have even started resolving, so something
   *  recognizable is on screen even on a very slow connection — not gated,
   *  not required to succeed (a 404 here just means nothing shows until the
   *  small tier lands, exactly like before this existed). */
  placeholder?: string | null;
  /** ~960px lightweight tier, when a ready one exists. Requested immediately,
   *  never gated — every block type gets a fast first paint. */
  small?: string | null;
  /** ~1600px tier, when a ready one exists. Requested on EVERY viewport
   *  (mobile included — a phone on a fast connection or a big/high-DPR
   *  screen benefits from it too) once the site-wide small-tier wave has
   *  settled; only `original` is used as the ceiling when no large tier
   *  exists. */
  large?: string | null;
  /** Always-available full source — the final fallback, and the ceiling when
   *  no `large` tier exists. */
  original: string;
  /** The tier this image is allowed to escalate all the way to once `large`
   *  has loaded. `'large'` (default) stops at the ~1600px tier forever —
   *  right for supporting surfaces (thumbnails, cards, nav logo) that are
   *  never displayed bigger than that on screen, so fetching the true
   *  original would just waste bandwidth. `'original'` continues on to the
   *  true full-resolution source in the background once `large` has
   *  rendered — for surfaces a visitor might view large/close-up (a
   *  full-bleed hero/section background, a lightbox) where 1600px can look
   *  soft on a high-DPI or ultra-wide display and "upgrade to max quality"
   *  should mean the actual original, not a capped tier. */
  finalTier?: 'large' | 'original';
  /** Hint: the CSS pixel width this element actually renders at (for
   *  full-bleed `cover` backgrounds pass `coverViewportPx()` — the larger
   *  viewport axis). Feeds the YouTube-style auto-quality policy
   *  (abrQuality.ts): a box the small tier already covers at this device's
   *  DPR never fetches bigger tiers. Read at each upgrade decision,
   *  deliberately NOT reactive — a later resize doesn't restage the ladder.
   *  No hint = no display cap (network policy still applies). */
  displayWidthPx?: number | null;
  /** Video-only hints: the original's byte size and duration, when the
   *  caller knows them (the owner-authenticated library popout does; the
   *  public site render path doesn't). Lets the policy compare measured
   *  throughput against the original's actual average bitrate instead of a
   *  coarse floor. */
  originalBytes?: number | null;
  durationSeconds?: number | null;
}

/**
 * The tier to escalate to once the small tier has settled — shared by every
 * hook below so the escalation rule can't drift between them. Every
 * viewport (mobile included) escalates the same way: the large tier when one
 * exists, else `original` as the ceiling. Returns null when no escalation is
 * needed (nothing left to fetch).
 */
function nextTierAfterSmall(large: string | null | undefined, original: string): string | null {
  return large || original;
}

// Only `placeholder` is safe to show unpreloaded — it's genuinely tiny
// (≤6KB, ~24px), so any visible streaming is imperceptible. EVERY other
// tier, including `small` (~960px, up to 3MB), is big enough to visibly
// stream in top-to-bottom on a real connection — confirmed in practice on
// product-gallery cards, which always have a real generated small tier (so
// they never hit the "nothing staged" branch below, and were showing THAT
// tier's own network fetch raw). small/large/original are all gated behind
// a confirmed-loaded preload in useProgressiveTierSrc's effect; the
// trade-off is a brief blank gap on fast gallery Prev/Next instead of an
// instant-but-visibly-loading image — never showing a half-downloaded photo
// wins over that. Nothing staged at all (no placeholder/small/large) → no
// smaller tier to offer before `original`, so show it right away —
// identical to no variant system.
function initialTier(
  placeholder: string | null | undefined, small: string | null | undefined,
  large: string | null | undefined, original: string,
): string {
  return placeholder || ((small || large) ? '' : original);
}

function preloadImage(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

/**
 * Coordinates ONE image's staged upgrade path — an instant placeholder (when
 * given) while nothing else has loaded yet, then the small (~960px) tier as
 * soon as it's confirmed loaded, then large/original only once every OTHER
 * in-flight small-tier load on the page has settled
 * (progressiveTierGate.ts). Shared by every image-bearing surface: hero/
 * section CSS backgrounds, library content `<img>`s, product gallery/cards.
 * Every device escalates all the way to the best available tier — the fast
 * small tier lands first everywhere, mobile included, but nothing stays
 * capped there.
 *
 * Self-preloads `small`/`large`/`original` with a throwaway `Image()` and
 * only ever commits one to the returned `src` once that preload's `onload`
 * has actually fired — never the real rendered element's own streaming
 * download. This is what stops a hero/section background OR a product-
 * gallery card from visibly filling in top-to-bottom as a multi-hundred-KB/
 * multi-MB tier streams over the network. Only `placeholder` is shown
 * immediately unpreloaded (see initialTier) — it's small enough (≤6KB) that
 * a visible stream is imperceptible; every bigger tier trades a possible
 * brief blank gap (e.g. on fast gallery Prev/Next) for never showing a
 * half-downloaded photo.
 *
 * `small`/`large` absent (CDN off, not generated yet) degrades to `original`
 * immediately, unpreloaded — this is the one case with nothing to gain from
 * staging (there's no smaller tier to show first), so it's identical to
 * rendering the source with no variant system at all, same as before this
 * hook existed.
 */
export function useProgressiveTierSrc({ placeholder, small, large, original, finalTier = 'large', displayWidthPx }: ProgressiveTierSources): string {
  const [resolved, setResolved] = useState(() => initialTier(placeholder, small, large, original));

  // Latest display hint, readable at upgrade-decision time without being an
  // effect dependency — a resize mid-stage must not restage the ladder.
  const displayWidthRef = useRef(displayWidthPx);
  displayWidthRef.current = displayWidthPx;

  // Reset DURING render (not in the effect below) when the source set
  // itself changes — e.g. a product card's gallery slide swapping to a
  // different item in place, with no remount. An effect-based reset runs
  // AFTER the browser has already painted, so for one frame the element
  // would show the PREVIOUS item's fully-resolved (large/original) image
  // before snapping to the new item's low tier — exactly the "shows the old
  // asset then suddenly snaps to the new one" bug on fast gallery paging.
  // Calling setState conditionally during render is the React-sanctioned
  // pattern for this (react.dev: "Adjusting state when a prop changes") —
  // React reruns the component immediately, before ever committing the
  // stale paint, and the key-comparison guard means it converges in one
  // extra pass, never loops.
  const key = [placeholder, small, large, original, finalTier].join(' ');
  const prevKeyRef = useRef(key);
  if (prevKeyRef.current !== key) {
    prevKeyRef.current = key;
    setResolved(initialTier(placeholder, small, large, original));
  }

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let bestSucceeded = false;
      if (small) {
        const release = registerSmallTierLoad();
        const ok = await preloadImage(small);
        release();
        if (cancelled) return;
        if (ok) { recordResourceThroughput(small); setResolved(small); bestSucceeded = true; }
        else if (!placeholder) setResolved(original);
      }

      await waitForLargeTierGate();
      if (cancelled) return;

      // Auto-quality decision, made AFTER the small wave settles so the
      // wave's own downloads have already fed the throughput estimate
      // (abrQuality.ts): stay on small when the display box or the network
      // says bigger tiers buy nothing. Only applied when small actually
      // rendered — a failed/absent small still needs SOME real tier, so the
      // legacy unconditional ladder runs (correctness over savings).
      let allowFinalOriginal = true;
      let midTier = nextTierAfterSmall(large, original);
      if (bestSucceeded) {
        const plan = planImageUpgrade({ large, original, displayCssPx: displayWidthRef.current });
        midTier = plan.midTier;
        allowFinalOriginal = plan.allowFinalOriginal;
      }
      if (midTier) {
        const ok = await preloadImage(midTier);
        if (cancelled) return;
        if (ok) { recordResourceThroughput(midTier); setResolved(midTier); bestSucceeded = true; }
        else if (!bestSucceeded) setResolved(original);
      }

      // Stage 3, opt-in only: `midTier` above already IS `original` whenever
      // no real `large` tier exists, so this only ever does extra work when
      // there's a genuine large→original gap to close. No extra gate wait —
      // by this point `large` has already rendered something presentable;
      // this is a pure background quality upgrade, not something other
      // in-flight images need to stay coordinated with.
      if (finalTier === 'original' && allowFinalOriginal && large && large !== original) {
        const ok = await preloadImage(original);
        if (cancelled) return;
        if (ok) setResolved(original);
      }
    })();

    return () => { cancelled = true; };
  }, [placeholder, small, large, original, finalTier]);

  return resolved;
}

/** Same staged tier sources as useProgressiveTierSrc, for CSS
 *  `background-image` (hero/section backgrounds) — an alias kept distinct so
 *  call sites document their intent; the implementation is identical (self-
 *  preloading works the same way whether the resolved URL ends up in an
 *  `<img src>` or a `background-image: url(...)`). */
export const useProgressiveTierBgImage = useProgressiveTierSrc;

export interface ProgressiveTierVideoResult {
  /** Currently-best-confirmed src — bind directly to <video src>. */
  src: string;
  /** Bind to <video>'s onLoadedData (fires once the first frame is
   *  available — the right "this tier is functional" signal for a streamed
   *  resource, unlike waiting for the whole file to download). */
  onLoad: () => void;
  /** Bind to <video>'s onError — falls back gracefully. */
  onError: () => void;
}

/**
 * Video counterpart of useProgressiveTierSrc: same small→large/original
 * staged escalation and site-wide gate, but DOM-event-driven (bound to the
 * real `<video>`'s onLoadedData/onError) rather than self-preloading — a
 * `<video>` is meant to stream incrementally via Range requests as it plays,
 * so forcing a full `fetch()`/`Image()`-style preload before ever showing it
 * would mean waiting for the ENTIRE file before any playback starts, a
 * severe regression vs. today. There's no `placeholder` tier here for the
 * same reason there's no need for one: the `poster` attribute already shows
 * something recognizable instantly, natively, with zero extra plumbing.
 *
 * `small` is typically the ~640px re-encoded low-bitrate `video_sm_mp4`;
 * there is currently no separate large video tier, so `large` is expected to
 * be omitted — escalation goes straight to `original` once the site-wide
 * wave settles, on every device (mobile included).
 */
export function useProgressiveTierVideoSrc({ small, large, original, displayWidthPx, originalBytes, durationSeconds }: ProgressiveTierSources): ProgressiveTierVideoResult {
  const [src, setSrc] = useState(() => small || original);
  const stageRef = useRef<'small' | 'final'>(small ? 'small' : 'final');
  const releaseRef = useRef<(() => void) | null>(null);
  const genRef = useRef(0);

  // Auto-quality hints, read at escalation time — not reactive (see
  // displayWidthPx doc on ProgressiveTierSources).
  const hintsRef = useRef({ displayWidthPx, originalBytes, durationSeconds });
  hintsRef.current = { displayWidthPx, originalBytes, durationSeconds };

  // A new source set (different media item entirely) resets the whole staged
  // sequence, so a stale event from the PREVIOUS item can never misfire a
  // stage transition for the new one.
  useEffect(() => {
    const gen = ++genRef.current;
    stageRef.current = small ? 'small' : 'final';
    setSrc(small || original);
    releaseRef.current?.();
    releaseRef.current = small ? registerSmallTierLoad() : null;
    return () => {
      releaseRef.current?.();
      releaseRef.current = null;
      void gen;
    };
  }, [small, large, original]);

  // `applyPolicy: false` is the broken-small fallback path — original is the
  // only playable source left, so the auto-quality veto must never apply.
  const escalate = (gen: number, applyPolicy: boolean) => {
    stageRef.current = 'final';
    const nextTier = nextTierAfterSmall(large, original);
    if (!nextTier) return;
    waitForLargeTierGate().then(() => {
      if (genRef.current !== gen) return;
      const h = hintsRef.current;
      if (applyPolicy && !planVideoUpgrade({
        displayCssPx: h.displayWidthPx,
        originalBytes: h.originalBytes,
        durationSeconds: h.durationSeconds,
      })) return;
      setSrc((cur) => (cur === nextTier ? cur : nextTier));
    });
  };

  const onLoad = () => {
    if (stageRef.current !== 'small') return;
    releaseRef.current?.();
    releaseRef.current = null;
    escalate(genRef.current, true);
  };
  const onError = () => {
    if (stageRef.current === 'small') {
      releaseRef.current?.();
      releaseRef.current = null;
      escalate(genRef.current, false);
    } else {
      setSrc((cur) => (cur === original ? cur : original));
    }
  };

  return { src, onLoad, onError };
}

/**
 * Seamless small→original video upgrade: attach the returned ref to a
 * `<video>` element (no `src` prop — this hook owns it imperatively) and it
 * plays the small tier immediately, then swaps to the full original ONLY
 * once that original has downloaded completely in the background, resuming
 * from the exact playback position (and play/pause state) the small tier
 * was at — never a jump back to 0:00, never a visible stall.
 *
 * This is deliberately a different strategy from useProgressiveTierVideoSrc
 * above: that hook just reassigns `src` declaratively, which makes the
 * browser reload the resource from scratch — a real stall/reset mid-
 * playback, not just a quality bump. `fetch(original).then(r => r.blob())`
 * downloads the WHOLE upgrade tier off-screen (this is fine here, unlike
 * initial playback: the small tier is already visibly playing throughout,
 * so there's no "wait for a full download before anything plays" regression
 * — only the currently-invisible upgrade is deferred until it's certain to
 * play through without its own buffering stall) and only swaps the visible
 * element's `.src` once that blob is fully in hand, immediately seeking to
 * the small tier's last `currentTime` and resuming play() if it was
 * playing. No `large` tier exists for video (see useProgressiveTierVideoSrc)
 * so this is a straight two-tier small→original escalation.
 *
 * `small` absent (CDN off, not generated, or equal to `original`) just plays
 * `original` directly with no swap logic at all — identical to a plain
 * `<video src>`.
 */
export function useProgressiveTierVideoRef(
  { small, original, displayWidthPx, originalBytes, durationSeconds }: {
    small?: string | null; original: string;
    displayWidthPx?: number | null; originalBytes?: number | null; durationSeconds?: number | null;
  },
): RefObject<HTMLVideoElement> {
  const ref = useRef<HTMLVideoElement>(null);
  // The blob URL currently applied to the element (if any) — revoked once a
  // later tier swap or unmount makes it unreachable, so a page with many
  // videos never leaks blob memory.
  const blobUrlRef = useRef<string | null>(null);

  // Auto-quality hints, read at upgrade-decision time — not reactive (see
  // displayWidthPx doc on ProgressiveTierSources).
  const hintsRef = useRef({ displayWidthPx, originalBytes, durationSeconds });
  hintsRef.current = { displayWidthPx, originalBytes, durationSeconds };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    const controller = new AbortController();

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    el.src = small || original;

    // Nothing to upgrade to — either no lightweight tier exists yet, or it's
    // (degenerately) the same URL as original.
    if (!small || small === original) {
      return () => { cancelled = true; controller.abort(); };
    }

    let releaseSmall: (() => void) | null = registerSmallTierLoad();
    const releaseOnce = () => { releaseSmall?.(); releaseSmall = null; };

    const upgrade = async () => {
      await waitForLargeTierGate();
      if (cancelled) return;
      // Auto-quality veto (abrQuality.ts): the small tier is playing fine —
      // don't spend a whole original download on a link/display that can't
      // benefit. The small tier keeps playing, same as a failed upgrade.
      const h = hintsRef.current;
      if (!planVideoUpgrade({
        displayCssPx: h.displayWidthPx,
        originalBytes: h.originalBytes,
        durationSeconds: h.durationSeconds,
      })) return;
      try {
        const fetchStart = performance.now();
        const res = await fetch(original, { signal: controller.signal });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        // A whole-file download we timed ourselves — the highest-quality
        // throughput sample this system ever sees; feed the estimator.
        recordTransferThroughput(blob.size, performance.now() - fetchStart);
        const blobUrl = URL.createObjectURL(blob);

        // Snapshot exactly where the small tier is RIGHT NOW, immediately
        // before swapping — not earlier (the visitor may have scrubbed or
        // paused since the fetch started).
        const resumeAt = el.currentTime;
        const wasPlaying = !el.paused;

        const onMeta = () => {
          el.removeEventListener('loadedmetadata', onMeta);
          if (cancelled) { URL.revokeObjectURL(blobUrl); return; }
          el.currentTime = resumeAt;
          if (wasPlaying) el.play().catch(() => {});
        };
        el.addEventListener('loadedmetadata', onMeta);
        el.src = blobUrl;
        blobUrlRef.current = blobUrl;
      } catch {
        // Aborted (unmount/item change) or a real network failure — silently
        // keep playing the small tier forever rather than risk a bad swap.
      }
    };

    const onSmallReady = () => {
      el.removeEventListener('loadeddata', onSmallReady);
      el.removeEventListener('error', onSmallError);
      releaseOnce();
      void upgrade();
    };
    const onSmallError = () => {
      el.removeEventListener('loadeddata', onSmallReady);
      el.removeEventListener('error', onSmallError);
      releaseOnce();
      // The small tier itself is broken — original is the only source left,
      // so just play it directly (unpreloaded; same graceful-degradation
      // contract every other tier failure in this system follows).
      if (!cancelled) el.src = original;
    };
    el.addEventListener('loadeddata', onSmallReady);
    el.addEventListener('error', onSmallError);

    return () => {
      cancelled = true;
      controller.abort();
      releaseOnce();
      el.removeEventListener('loadeddata', onSmallReady);
      el.removeEventListener('error', onSmallError);
    };
  }, [small, original]);

  // Revoke whatever blob URL is still applied when this element's hook
  // instance itself goes away for good (as opposed to just swapping tiers).
  useEffect(() => () => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
  }, []);

  return ref;
}
